import { Text, Button, Grid, Paper, useMantineTheme, Box, Overlay } from '@mantine/core'
import { useDisclosure, useMediaQuery, useTimeout } from '@mantine/hooks'
import { FC, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaFire, FaQuestionCircle, FaBackspace, FaExclamationTriangle, FaCheck } from 'react-icons/fa'
import { FaArrowsSplitUpAndLeft } from 'react-icons/fa6'
import { MapRef } from 'react-map-gl/mapbox'
import { useMutation } from '@tanstack/react-query'
import { FiNavigation } from 'react-icons/fi'
import { GiSandsOfTime } from 'react-icons/gi'

import { UnexpectedEventType } from '@/api/main/types.gen'
import { reportUnexpectedEventMutation } from '@/api/navigationApi'
import MapControlBox from './MapControlBox'
import { showNotification } from '@mantine/notifications'
import notificationStyles from './Notification.module.css'
import { Client } from '@/api/main/client'

const NavigationDialog: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Box
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Overlay />
      <Box style={{ zIndex: 10000 }}>{children}</Box>
    </Box>
  )
}

const UnexpectedEventButton: FC<{
  visible: boolean
  navigationMap?: MapRef
  client: Client | undefined
}> = ({ visible, client }) => {
  const { t } = useTranslation(['unexpectedEvents', 'alerts'])
  const [dialogOpen, dialogHandlers] = useDisclosure(false)
  const fontSize = `calc(1.8 * ${useMantineTheme().fontSizes.xl})`

  const [isUnexpectedEventDisabled, setUnexpectedEventDisabled] = useState(false)
  const { start: timeoutUnexpectedEventButton } = useTimeout(() => setUnexpectedEventDisabled(false), 1000 * 10)
  const isLandscape = useMediaQuery(`(orientation: landscape)`)

  const {
    mutate: reportUnexpectedEvent,
    status,
    variables,
  } = useMutation({
    ...reportUnexpectedEventMutation({ client }),
    onMutate: () => {
      setUnexpectedEventDisabled(true)
      timeoutUnexpectedEventButton()
    },
    onSuccess: () => {
      dialogHandlers.close()
      showNotification({
        color: 'green',
        message: t('unexpectedEventReported', { ns: 'alerts' }),
        position: 'bottom-center',
        autoClose: true,
        icon: <FaCheck />,
        classNames: notificationStyles,
        withCloseButton: false,
      })
    },
  })

  const handleClick = (eventType: UnexpectedEventType) => reportUnexpectedEvent({ body: { event_type: eventType } })

  const labels: Partial<Record<UnexpectedEventType, { title: string; icon: ReactNode }>> = {
    ['road_temporarily_blocked']: {
      title: t('roadTemporarilyBlocked'),
      icon: <GiSandsOfTime />,
    },
    ['turn_not_possible']: {
      title: t('turnNotPossible'),
      icon: <FaArrowsSplitUpAndLeft />,
    },
    ['road_closed']: {
      title: t('roadClosed'),
      icon: <FaExclamationTriangle />,
    },
    ['inaccurate_gps']: {
      title: t('inaccurateGps'),
      icon: <FiNavigation />,
    },
    ['unknown']: { title: t('unknown'), icon: <FaQuestionCircle /> },
  }

  return (
    <>
      {dialogOpen && (
        <NavigationDialog>
          <Paper style={{ width: '100vw', height: '100vh' }}>
            <Grid gutter="xl" align="center">
              {Object.entries(labels).map(([key, item]) => (
                <Grid.Col
                  key={key}
                  span={
                    isLandscape
                      ? key === 'road_temporarily_blocked'
                        ? 8
                        : 4
                      : key === 'road_temporarily_blocked'
                        ? 12
                        : 6
                  }
                >
                  <Button
                    color="lime"
                    styles={{
                      root: { minHeight: isLandscape ? '40vh' : '27vh', width: '100%' },
                      section: { fontSize },
                      label: { whiteSpace: 'normal', textAlign: 'center' },
                    }}
                    onClick={() => handleClick(key as UnexpectedEventType)}
                    loading={status === 'pending' && variables?.body?.event_type === key}
                    leftSection={item.icon}
                  >
                    <Text size={fontSize} color="white" style={{ textAlign: 'center' }}>
                      {item.title}
                    </Text>
                  </Button>
                </Grid.Col>
              ))}
              <Grid.Col span={12}>
                <Button
                  color="red"
                  onClick={dialogHandlers.close}
                  styles={{
                    root: {
                      minHeight: '10vh',
                      width: '100%',
                    },
                    section: { fontSize },
                  }}
                  leftSection={<FaBackspace />}
                >
                  <Text size={fontSize} color="white">
                    {t('back')}
                  </Text>
                </Button>
              </Grid.Col>
            </Grid>
          </Paper>
        </NavigationDialog>
      )}
      {visible && (
        <MapControlBox position="left" style={{ top: 40 }}>
          <Button
            color="red"
            onClick={e => {
              e.stopPropagation()
              dialogHandlers.open()
            }}
            disabled={isUnexpectedEventDisabled}
            styles={{
              root: {
                height: 60,
                borderRadius: 64,
                padding: '0 10px',
                fontSize: 40,
              },
            }}
          >
            <FaFire />
          </Button>
        </MapControlBox>
      )}
    </>
  )
}

export default UnexpectedEventButton
